"use client";
import type { SurveyQuestion, ContentRichtextOptions } from "@/types/survey";
import DOMPurify from "dompurify";

interface ContentRichtextBlockProps {
  question: SurveyQuestion;
}

export default function ContentRichtextBlock({
  question,
}: ContentRichtextBlockProps) {
  const opts = (question.options || {}) as ContentRichtextOptions;
  const htmlContent = opts.content || "";

  if (!htmlContent) return null;

  const sanitizedHtml =
    typeof window !== "undefined"
      ? DOMPurify.sanitize(htmlContent, { ADD_ATTR: ["style"] })
      : htmlContent;

  return (
    <div style={{ maxWidth: opts.maxWidth || "100%" }}>
      <style>{`
        /* Inherit the survey theme's text color so dark-mode surveys
           render rich text in the theme's foreground instead of a
           hard-coded near-black. Explicit inline colors in the HTML
           still win since they're applied directly to elements. */
        .survey-content-richtext {
          color: inherit;
          text-align: left;
        }
        .survey-content-richtext > *:first-child {
          margin-top: 0;
        }
        .survey-content-richtext > *:last-child {
          margin-bottom: 0;
        }
        .survey-content-richtext h1,
        .survey-content-richtext h2,
        .survey-content-richtext h3,
        .survey-content-richtext h4,
        .survey-content-richtext h5,
        .survey-content-richtext h6 {
          color: inherit;
          font-weight: 600;
          margin-top: 1em;
          margin-bottom: 0.5em;
        }
        .survey-content-richtext h1 { font-size: 1.75rem; }
        .survey-content-richtext h2 { font-size: 1.5rem; }
        .survey-content-richtext h3 { font-size: 1.25rem; }
        .survey-content-richtext p {
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .survey-content-richtext ul,
        .survey-content-richtext ol {
          margin: 0.5em 0;
          padding-left: 1.5rem;
          color: inherit;
        }
        .survey-content-richtext ul {
          list-style-type: disc;
        }
        .survey-content-richtext ol {
          list-style-type: decimal;
        }
        .survey-content-richtext ul ul {
          list-style-type: circle;
        }
        .survey-content-richtext ul ul ul {
          list-style-type: square;
        }
        .survey-content-richtext li {
          margin: 0.25em 0;
        }
        .survey-content-richtext li > p {
          margin: 0;
        }
        .survey-content-richtext li::marker {
          color: inherit;
        }
        .survey-content-richtext a {
          color: var(--color-alias-info);
          text-decoration: underline;
        }
        .survey-content-richtext blockquote {
          margin: 0.75em 0;
          padding-left: 1rem;
          border-left: 4px solid var(--color-alias-border-neutral);
          color: inherit;
          opacity: 0.85;
        }
        .survey-content-richtext strong {
          color: inherit;
        }
      `}</style>
      <div
        className="survey-content-richtext prose max-w-none"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </div>
  );
}
